import { useState, useRef, useCallback, useEffect } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { Popover } from 'foundations-components/transitional/components/Popover';
import { View } from 'foundations-components/transitional/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { Link } from 'foundations-components/transitional/components/Link';
import { AddIcon } from 'foundations-assets/icons/components/AddIcon';
import { useIsPillBorderRadius } from '../../hooks/useIsPillBorderRadius';
import { getIsThemePreview } from '../../utils/metadata';
import { trackCreateSpaceClicked } from '../../utils/usageTracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
// Landing page (a CMS route, not an in-app SPA route) that lets the contact
// pick which space to create when more than one type is available.
const ADD_SPACE_PATH = '/hs/portals/app/add-space';
export function CreateSpaceNavItem({
  creatableSpaces,
  history,
  isExpanded = false
}) {
  var _creatableSpaces$0$se, _creatableSpaces$;
  const [open, setOpen] = useState(false);
  const closeTimeout = useRef(undefined);
  const isPill = useIsPillBorderRadius();
  useEffect(() => () => clearTimeout(closeTimeout.current), []);
  const show = useCallback(() => {
    clearTimeout(closeTimeout.current);
    setOpen(true);
  }, []);
  const hide = useCallback(() => {
    closeTimeout.current = setTimeout(() => setOpen(false), 150);
  }, []);

  // A single creatable space links straight into its setup flow (an in-app,
  // type-relative path we can client-navigate). More than one goes to the
  // add-space landing page, which is a CMS route and always a hard navigation.
  const hasMultiple = creatableSpaces.length > 1;
  const href = hasMultiple ? ADD_SPACE_PATH : (_creatableSpaces$0$se = (_creatableSpaces$ = creatableSpaces[0]) === null || _creatableSpaces$ === void 0 ? void 0 : _creatableSpaces$.setupPath) !== null && _creatableSpaces$0$se !== void 0 ? _creatableSpaces$0$se : ADD_SPACE_PATH;
  const handleClick = useCallback(e => {
    if (getIsThemePreview()) {
      e.preventDefault();
      return;
    }
    trackCreateSpaceClicked({
      creatableSpaceCount: creatableSpaces.length,
      destination: hasMultiple ? 'landing' : 'setup'
    });
    if (!hasMultiple && history) {
      e.preventDefault();
      history.push(href);
    }
  }, [creatableSpaces.length, hasMultiple, history, href]);
  if (creatableSpaces.length === 0) return null;
  const label = unescapedText('app.sideNav.createSpace');
  const iconBorderRadius = isPill ? '0' : '300';
  const iconSlot = /*#__PURE__*/_jsx(Flex, {
    inlineSize: "40px",
    blockSize: "40px",
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
    children: /*#__PURE__*/_jsx(AddIcon, {
      color: "text-core-default",
      size: "500",
      "aria-hidden": true
    })
  });
  const trigger = /*#__PURE__*/_jsx(Flex, {
    borderRadius: iconBorderRadius,
    inlineSize: isExpanded ? '100%' : '40px',
    blockSize: "40px",
    backgroundColor: open ? 'fill-accent-neutral-subtle' : undefined,
    children: /*#__PURE__*/_jsxs(Link, {
      href: href,
      use: "unstyled",
      display: "flex",
      inlineSize: "100%",
      blockSize: "100%",
      alignItems: "center",
      justifyContent: isExpanded ? undefined : 'center',
      paddingInlineEnd: isExpanded ? '200' : undefined,
      "aria-label": label,
      onClick: handleClick,
      children: [iconSlot, isExpanded && /*#__PURE__*/_jsx(View, {
        as: "span",
        typography: "body-100",
        color: "text-core-default",
        whiteSpace: "nowrap",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "app.sideNav.createSpace"
        })
      })]
    })
  });
  return /*#__PURE__*/_jsx(View, {
    "data-test-id": "portals-side-nav-create-space",
    onMouseEnter: show,
    onMouseLeave: hide,
    position: "relative",
    children: isExpanded ? trigger : /*#__PURE__*/_jsx(Popover, Object.assign({
      open: open,
      onOpenChange: ({
        target: {
          value
        }
      }) => setOpen(value),
      content: /*#__PURE__*/_jsx(View, {
        onMouseEnter: show,
        onMouseLeave: hide,
        paddingBlock: "100",
        paddingInline: "200",
        children: /*#__PURE__*/_jsx(View, {
          as: "span",
          typography: "body-100",
          color: "text-core-default",
          children: label
        })
      }),
      placement: "right bottom",
      arrowSize: "none",
      closeOnOutsideClick: false
    }, isPill && {
      'data-nav-label-popover': true
    }, {
      children: trigger
    }))
  });
}